import { testFirebaseConnection } from './test-firebase';

// src/App.js
import React, { useState, useEffect } from 'react';
import './App.css';

// Contextos e Hooks
import { useAuth } from './contexts/AuthContext';

// Serviços Firebase
import {
  saveAppointment,
  getAppointmentsByPhone,
  getAllAppointments,
  cancelAppointment as cancelFirebaseAppointment,
  getServices,
  syncLocalWithFirebase
} from './firebase/services';

// Utilitários
import { saveAppointment as saveLocalAppointment, getAppointmentsByPhone as getLocalAppointments } from './utils/storage';

// Componentes
import PixPayment from './components/PixPayment';

function App() {
  // ============== ESTADOS PRINCIPAIS ==============
  const [screen, setScreen] = useState('home');
  const [userPhone, setUserPhone] = useState('');
  const [userAppointments, setUserAppointments] = useState([]);
  const [allAppointments, setAllAppointments] = useState([]); // Para barbeiro/admin
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isBarbeiro, setIsBarbeiro] = useState(false);

  // ============== ESTADOS DE AUTENTICAÇÃO ==============
  const { user, signInWithPhone, verifyCode, signOut, loading: authLoading } = useAuth();
  const [showCodeVerification, setShowCodeVerification] = useState(false);
  const [tempPhone, setTempPhone] = useState('');
  const [codeInput, setCodeInput] = useState('');
  const [authError, setAuthError] = useState('');

  // ============== ESTADOS DE PAGAMENTO PIX ==============
  const [showPixPayment, setShowPixPayment] = useState(false);
  const [currentAppointmentData, setCurrentAppointmentData] = useState(null);
  const [paymentStep, setPaymentStep] = useState('form'); // form, pix, confirmation

  // ============== ESTADOS DE INSTALAÇÃO PWA ==============
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstallButton, setShowInstallButton] = useState(false);

  // ============== EFEITOS ==============

  // Efeito para PWA install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstallButton(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  // Efeito para carregar dados iniciais
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        // Carrega serviços do Firebase
        const servicesData = await getServices();
        setServices(servicesData);

        // Se usuário está logado, carrega seus agendamentos
        if (user) {
          setUserPhone(user.phoneNumber || '');
          
          // Verifica se é barbeiro (simulação)
          const isBarber = user.phoneNumber?.includes('5511999999999'); // Número de teste
          setIsBarbeiro(isBarber);
          
          // Sincroniza localStorage com Firebase
          await syncLocalWithFirebase({}, user.phoneNumber);
          
          // Carrega agendamentos do Firebase
          const appointments = await getAppointmentsByPhone(user.phoneNumber);
          setUserAppointments(appointments);
          
          // Se for barbeiro, carrega todos agendamentos
          if (isBarber) {
            const allApps = await getAllAppointments();
            setAllAppointments(allApps);
          }
        }
      } catch (error) {
        console.error('❌ Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [user]);

  // ============== HANDLERS PRINCIPAIS ==============

  // Handler para instalar PWA
  const handleInstallClick = async () => {
    if (!installPrompt) return;
    
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setShowInstallButton(false);
    }
    
    setInstallPrompt(null);
  };

  // Handler para login com telefone
  const handleLogin = async (phone) => {
    setAuthError('');
    setTempPhone(phone);
    
    const result = await signInWithPhone(phone);
    if (result.success) {
      setShowCodeVerification(true);
    } else {
      setAuthError(result.message);
    }
  };

  // Handler para verificar código
  const handleVerifyCode = async () => {
    setAuthError('');
    
    const result = await verifyCode(codeInput);
    if (result.success) {
      setShowCodeVerification(false);
      setCodeInput('');
      setTempPhone('');
      
      // Atualiza telefone do usuário
      setUserPhone(result.user.phoneNumber);
      
      // Carrega agendamentos
      const appointments = await getAppointmentsByPhone(result.user.phoneNumber);
      setUserAppointments(appointments);
    } else {
      setAuthError(result.message);
    }
  };

  // Handler para logout
  const handleLogout = async () => {
    await signOut();
    setUserPhone('');
    setUserAppointments([]);
    setScreen('home');
  };

  // Handler para salvar agendamento
  const handleSaveAppointment = async (appointmentData, paymentMethod = 'pix') => {
    try {
      // Se o usuário não está logado, usa localStorage
      if (!user) {
        const saved = saveLocalAppointment(appointmentData);
        setUserAppointments(prev => [...prev, saved]);
        alert('✅ Agendamento salvo localmente! Faça login para sincronizar na nuvem.');
        return saved;
      }

      // Se está logado, salva no Firebase
      const saved = await saveAppointment(appointmentData);
      
      // Atualiza lista local
      setUserAppointments(prev => [...prev, saved]);
      
      // Se for barbeiro, atualiza lista completa
      if (isBarbeiro) {
        const allApps = await getAllAppointments();
        setAllAppointments(allApps);
      }

      // Se pagamento é PIX, mostra tela de pagamento
      if (paymentMethod === 'pix') {
        setCurrentAppointmentData({
          ...appointmentData,
          id: saved.id
        });
        setPaymentStep('pix');
        setScreen('payment');
      } else {
        // Pagamento na hora (presencial)
        alert(`✅ Agendamento confirmado!\n\nPor favor, pague R$ ${appointmentData.preco} na hora.`);
        setScreen('home');
      }

      return saved;
    } catch (error) {
      console.error('❌ Erro ao salvar agendamento:', error);
      
      // Fallback para localStorage
      const saved = saveLocalAppointment(appointmentData);
      setUserAppointments(prev => [...prev, saved]);
      alert('⚠️ Agendamento salvo localmente (Firebase offline).');
      
      return saved;
    }
  };

  // Handler para cancelar agendamento
  const handleCancelAppointment = async (appointmentId) => {
    if (window.confirm('Tem certeza que deseja cancelar este agendamento?')) {
      try {
        // Cancela no Firebase (se logado)
        if (user) {
          await cancelFirebaseAppointment(appointmentId);
        }
        
        // Atualiza estado local
        setUserAppointments(prev => 
          prev.map(app => 
            app.id === appointmentId 
              ? { ...app, status: 'cancelado' } 
              : app
          )
        );
        
        alert('✅ Agendamento cancelado com sucesso!');
      } catch (error) {
        console.error('❌ Erro ao cancelar agendamento:', error);
        alert('⚠️ Agendamento cancelado localmente.');
      }
    }
  };

  // Handler para sucesso no pagamento PIX
  const handlePixSuccess = (txid) => {
    alert(`✅ Pagamento confirmado! TXID: ${txid}\nSeu agendamento está confirmado.`);
    setShowPixPayment(false);
    setPaymentStep('form');
    setScreen('home');
  };

  // ============== RENDERIZAÇÃO DE TELAS ==============

  const renderScreen = () => {
    if (loading || authLoading) {
      return (
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Carregando...</p>
        </div>
      );
    }

    switch(screen) {
      case 'home':
        return (
          <HomeScreen
            user={user}
            userPhone={userPhone}
            onLogin={handleLogin}
            onLogout={handleLogout}
            goTo={setScreen}
            isBarbeiro={isBarbeiro}
          />
        );

      case 'agendar':
        return (
          <AgendamentoScreen
            goBack={() => setScreen('home')}
            userPhone={userPhone}
            services={services}
            onSave={handleSaveAppointment}
            onPixPayment={() => setPaymentStep('pix')}
          />
        );

      case 'meus-agendamentos':
        return (
          <MeusAgendamentosScreen
            goBack={() => setScreen('home')}
            appointments={userAppointments}
            onCancel={handleCancelAppointment}
          />
        );

      case 'servicos':
        return (
          <ServicosScreen
            goBack={() => setScreen('home')}
            services={services}
          />
        );

      case 'payment':
        return (
          <PaymentScreen
            step={paymentStep}
            appointmentData={currentAppointmentData}
            onPixSuccess={handlePixSuccess}
            onBack={() => {
              setPaymentStep('form');
              setScreen('agendar');
            }}
            onComplete={() => {
              setPaymentStep('form');
              setScreen('home');
            }}
          />
        );

      case 'barbeiro':
        return (
          <BarbeiroDashboard
            goBack={() => setScreen('home')}
            appointments={allAppointments}
            onCancel={handleCancelAppointment}
          />
        );

      default:
        return (
          <div className="screen-default">
            <h2>Tela em desenvolvimento</h2>
            <button onClick={() => setScreen('home')} className="btn-primary">
              Voltar
            </button>
          </div>
        );
    }
  };

  // ============== RENDER PRINCIPAL ==============
  return (
    <div className="App">
      {/* Banner de instalação PWA */}
      {showInstallButton && (
        <div className="install-banner">
          <p>📱 Instale nosso app para melhor experiência!</p>
          <button onClick={handleInstallClick} className="install-btn">
            Instalar App
          </button>
        </div>
      )}

      {/* Modal de verificação de código */}
      {showCodeVerification && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>🔐 Verificação de Código</h3>
            <p>Enviamos um código para {tempPhone}</p>
            
            <div className="form-group">
              <input
                type="text"
                value={codeInput}
                onChange={(e) => setCodeInput(e.target.value)}
                placeholder="Digite o código de 6 dígitos"
                maxLength={6}
                className="code-input"
              />
            </div>

            {authError && <div className="auth-error">{authError}</div>}

            <div className="modal-actions">
              <button 
                onClick={() => setShowCodeVerification(false)} 
                className="btn-secondary"
              >
                Cancelar
              </button>
              <button 
                onClick={handleVerifyCode} 
                className="btn-primary"
                disabled={codeInput.length !== 6}
              >
                Verificar
              </button>
            </div>

            <p className="code-hint">
              💡 Em desenvolvimento, o código aparece no console do navegador.
            </p>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          {screen !== 'home' && (
            <button 
              className="back-btn" 
              onClick={() => setScreen('home')}
            >
              ←
            </button>
          )}
          
          <h1 className="app-title">✂️ Barbearia </h1>
          
          {userPhone && (
            <div className="user-menu">
              <span className="user-phone">{userPhone}</span>
              {isBarbeiro && (
                <button 
                  className="barbeiro-btn"
                  onClick={() => setScreen('barbeiro')}
                >
                  👨‍✂️ Painel
                </button>
              )}
              <button 
                className="logout-btn"
                onClick={handleLogout}
              >
                Sair
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Conteúdo principal */}
      <main className="app-main">
        {renderScreen()}
      </main>

      {/* Footer */}
      <footer className="app-footer">
        <div className="footer-content">
          <p>© 2025 Barbearia - Agendamento Online</p>
          <div className="footer-links">
            <button onClick={() => setScreen('servicos')}>Serviços</button>
            <button onClick={() => window.open('https://wa.me/5511999999999', '_blank')}>Contato</button>
            <button onClick={() => setScreen('meus-agendamentos')}>Meus Agendamentos</button>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============== COMPONENTES DE TELA ==============

// Tela Home
function HomeScreen({ user, userPhone, onLogin, onLogout, goTo, isBarbeiro }) {
  const [phoneInput, setPhoneInput] = useState(userPhone || '');
  const [loginError, setLoginError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoginError('');
    
    if (!phoneInput.trim()) {
      setLoginError('Digite um número de telefone');
      return;
    }

    // Validação simples de telefone
    const phoneRegex = /^\(?\d{2}\)?[\s-]?9?\d{4}[\s-]?\d{4}$/;
    if (!phoneRegex.test(phoneInput)) {
      setLoginError('Digite um telefone válido (ex: 11999999999)');
      return;
    }

    onLogin(phoneInput);
  };

  return (
    <div className="home-screen">
      <div className="hero">
        <h1>✂️ Barbearia</h1>
        <p className="tagline">Agende seu horário de forma rápida e fácil!</p>
        
        {!user ? (
          <div className="login-section">
            <form onSubmit={handleSubmit} className="login-form">
              <h3>🔐 Acesse sua conta</h3>
              
              <div className="form-group">
                <label>Digite seu WhatsApp:</label>
                <input
                  type="tel"
                  value={phoneInput}
                  onChange={(e) => setPhoneInput(e.target.value)}
                  placeholder="(11) 99999-9999"
                  required
                />
              </div>

              {loginError && <div className="error-message">{loginError}</div>}

              <button type="submit" className="btn-primary btn-block">
                Continuar com SMS
              </button>

              <p className="login-note">
                📱 Enviaremos um código de verificação por SMS
              </p>
            </form>

            <div className="guest-option">
              <button 
                onClick={() => {
                  const guestPhone = `55${Math.floor(Math.random() * 900000000) + 100000000}`;
                  onLogin(guestPhone);
                }}
                className="btn-secondary btn-block"
              >
                👤 Continuar como visitante
              </button>
              <small>Seus dados serão salvos apenas neste dispositivo</small>
            </div>
          </div>
        ) : (
          <div className="welcome-section">
            <div className="welcome-card">
              <h2>👋 Olá, {userPhone}!</h2>
              <p>O que você gostaria de fazer hoje?</p>
              
              <div className="action-grid">
                <button 
                  className="action-card primary"
                  onClick={() => goTo('agendar')}
                >
                  <span className="action-icon">✏️</span>
                  <span className="action-text">Agendar Horário</span>
                </button>
                
                <button 
                  className="action-card"
                  onClick={() => goTo('meus-agendamentos')}
                >
                  <span className="action-icon">📅</span>
                  <span className="action-text">Meus Agendamentos</span>
                </button>
                
                <button 
                  className="action-card"
                  onClick={() => goTo('servicos')}
                >
                  <span className="action-icon">✂️</span>
                  <span className="action-text">Serviços e Preços</span>
                </button>

                {isBarbeiro && (
                  <button 
                    className="action-card barbeiro"
                    onClick={() => goTo('barbeiro')}
                  >
                    <span className="action-icon">👨‍✂️</span>
                    <span className="action-text">Painel do Barbeiro</span>
                  </button>
                )}
              </div>
              
              <div className="user-actions">
                <button 
                  onClick={onLogout}
                  className="btn-tertiary btn-block"
                >
                  🔓 Sair da Conta
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Features */}
      <div className="features">
        <h3>✨ Por que usar nosso app?</h3>
        <div className="features-grid">
          <div className="feature">
            <span className="feature-icon">⏰</span>
            <h4>Agendamento Rápido</h4>
            <p>Agende em menos de 2 minutos</p>
          </div>
          <div className="feature">
            <span className="feature-icon">💳</span>
            <h4>Pagamento por PIX</h4>
            <p>Pague online com segurança</p>
          </div>
          <div className="feature">
            <span className="feature-icon">📱</span>
            <h4>App Instalável</h4>
            <p>Use como app no celular</p>
          </div>
          <div className="feature">
            <span className="feature-icon">🔔</span>
            <h4>Lembretes</h4>
            <p>Não perca seu horário</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// Tela de Agendamento
function AgendamentoScreen({ goBack, userPhone, services, onSave, onPixPayment }) {
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState(userPhone || '');
  const [servico, setServico] = useState('');
  const [barbeiro, setBarbeiro] = useState('');
  const [data, setData] = useState('');
  const [hora, setHora] = useState('');
  const [pagamento, setPagamento] = useState('pix');
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const barbeiros = [
    { id: 'joao', nome: 'João Silva', especialidade: 'Cortes modernos' },
    { id: 'carlos', nome: 'Carlos Santos', especialidade: 'Barba e bigode' },
    { id: 'marcos', nome: 'Marcos Oliveira', especialidade: 'Cortes clássicos' },
  ];

  const horariosDisponiveis = ['09:00', '10:00', '11:00', '14:00', '15:00', '16:00', '17:00'];

  const servicoSelecionado = services.find(s => s.nome === servico);
  const preco = servicoSelecionado?.preco || 0;

  const validateForm = () => {
    const newErrors = {};
    
    if (!nome.trim()) newErrors.nome = 'Nome é obrigatório';
    if (!telefone.trim()) newErrors.telefone = 'Telefone é obrigatório';
    if (!servico) newErrors.servico = 'Selecione um serviço';
    if (!data) newErrors.data = 'Selecione uma data';
    if (!hora) newErrors.hora = 'Selecione um horário';
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    const appointmentData = {
      nome,
      telefone,
      servico,
      barbeiro: barbeiro || 'Qualquer disponível',
      preco,
      data,
      hora,
      pagamento,
      status: pagamento === 'pix' ? 'aguardando_pagamento' : 'confirmado'
    };
    
    await onSave(appointmentData, pagamento);
    
    setIsSubmitting(false);
  };

  const formatDate = (dateString) => {
    const options = { weekday: 'long', day: 'numeric', month: 'long' };
    return new Date(dateString).toLocaleDateString('pt-BR', options);
  };

  return (
    <div className="agendamento-screen">
      <div className="screen-header">
        <h2>📅 Novo Agendamento</h2>
        <p>Preencha os dados para agendar seu horário</p>
      </div>
      
      <form onSubmit={handleSubmit} className="agendamento-form">
        <div className="form-section">
          <h3>👤 Dados Pessoais</h3>
          
          <div className="form-group">
            <label>Nome Completo *</label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Seu nome completo"
              className={errors.nome ? 'input-error' : ''}
            />
            {errors.nome && <span className="error-message">{errors.nome}</span>}
          </div>
          
          <div className="form-group">
            <label>WhatsApp *</label>
            <input
              type="tel"
              value={telefone}
              onChange={(e) => setTelefone(e.target.value)}
              placeholder="(11) 99999-9999"
              className={errors.telefone ? 'input-error' : ''}
              disabled={!!userPhone}
            />
            {errors.telefone && <span className="error-message">{errors.telefone}</span>}
          </div>
        </div>
        
        <div className="form-section">
          <h3>✂️ Serviço</h3>
          
          <div className="form-group">
            <label>Serviço Desejado *</label>
            <select 
              value={servico} 
              onChange={(e) => setServico(e.target.value)}
              className={errors.servico ? 'input-error' : ''}
            >
              <option value="">Selecione um serviço</option>
              {services.map(s => (
                <option key={s.id} value={s.nome}>
                  {s.nome} - R$ {s.preco} ({s.duracao}min)
                </option>
              ))}
            </select>
            {errors.servico && <span className="error-message">{errors.servico}</span>}
          </div>
          
          <div className="form-group">
            <label>Prefere algum barbeiro?</label>
            <select 
              value={barbeiro} 
              onChange={(e) => setBarbeiro(e.target.value)}
            >
              <option value="">Qualquer disponível</option>
              {barbeiros.map(b => (
                <option key={b.id} value={b.nome}>
                  {b.nome} - {b.especialidade}
                </option>
              ))}
            </select>
          </div>
        </div>
        
        <div className="form-section">
          <h3>⏰ Data e Horário</h3>
          
          <div className="form-row">
            <div className="form-group">
              <label>Data *</label>
              <input
                type="date"
                value={data}
                onChange={(e) => setData(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                max={new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                className={errors.data ? 'input-error' : ''}
              />
              {errors.data && <span className="error-message">{errors.data}</span>}
            </div>
            
            <div className="form-group">
              <label>Horário *</label>
              <select 
                value={hora} 
                onChange={(e) => setHora(e.target.value)}
                className={errors.hora ? 'input-error' : ''}
              >
                <option value="">Selecione um horário</option>
                {horariosDisponiveis.map(h => (
                  <option key={h} value={h}>{h}</option>
                ))}
              </select>
              {errors.hora && <span className="error-message">{errors.hora}</span>}
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h3>💳 Pagamento</h3>
          
          <div className="payment-options">
            <label className="payment-option">
              <input
                type="radio"
                name="pagamento"
                value="pix"
                checked={pagamento === 'pix'}
                onChange={(e) => setPagamento(e.target.value)}
              />
              <div className="payment-card">
                <span className="payment-icon">💳</span>
                <div>
                  <h4>Pagar com PIX</h4>
                  <p>Pague online e garanta seu horário</p>
                  <small>Desconto de 5%: R$ {(preco * 0.95).toFixed(2)}</small>
                </div>
              </div>
            </label>
            
            <label className="payment-option">
              <input
                type="radio"
                name="pagamento"
                value="presencial"
                checked={pagamento === 'presencial'}
                onChange={(e) => setPagamento(e.target.value)}
              />
              <div className="payment-card">
                <span className="payment-icon">💵</span>
                <div>
                  <h4>Pagar na hora</h4>
                  <p>Pague quando chegar na barbearia</p>
                  <small>Valor total: R$ {preco.toFixed(2)}</small>
                </div>
              </div>
            </label>
          </div>
        </div>
        
        {/* Resumo */}
        {servico && data && hora && (
          <div className="resumo-section">
            <h3>📋 Resumo do Agendamento</h3>
            <div className="resumo-card">
              <div className="resumo-item">
                <span>Serviço:</span>
                <strong>{servico}</strong>
              </div>
              <div className="resumo-item">
                <span>Barbeiro:</span>
                <strong>{barbeiro || 'Qualquer disponível'}</strong>
              </div>
              <div className="resumo-item">
                <span>Data:</span>
                <strong>{formatDate(data)}</strong>
              </div>
              <div className="resumo-item">
                <span>Horário:</span>
                <strong>{hora}</strong>
              </div>
              <div className="resumo-item total">
                <span>Valor:</span>
                <strong>R$ {preco.toFixed(2)}</strong>
              </div>
              {pagamento === 'pix' && (
                <div className="resumo-item desconto">
                  <span>Com desconto PIX:</span>
                  <strong>R$ {(preco * 0.95).toFixed(2)}</strong>
                </div>
              )}
            </div>
          </div>
        )}
        
        <div className="form-actions">
          <button 
            type="button" 
            className="btn-secondary"
            onClick={goBack}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button 
            type="submit" 
            className="btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Salvando...' : (
              pagamento === 'pix' ? 'Continuar para PIX' : 'Confirmar Agendamento'
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

// Tela Meus Agendamentos
function MeusAgendamentosScreen({ goBack, appointments, onCancel }) {
  const [filter, setFilter] = useState('todos');

  const filteredAppointments = appointments.filter(app => {
    if (filter === 'todos') return true;
    return app.status === filter;
  });

  const formatDate = (dateString, hora) => {
    const date = new Date(dateString);
    const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
    return `${date.toLocaleDateString('pt-BR', options)} às ${hora}`;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'confirmado': return '#28a745';
      case 'cancelado': return '#dc3545';
      case 'aguardando_pagamento': return '#ffc107';
      case 'concluido': return '#17a2b8';
      default: return '#6c757d';
    }
  };

  const getStatusText = (status) => {
    switch(status) {
      case 'confirmado': return 'Confirmado';
      case 'cancelado': return 'Cancelado';
      case 'aguardando_pagamento': return 'Aguardando PIX';
      case 'concluido': return 'Concluído';
      default: return status;
    }
  };

  return (
    <div className="meus-agendamentos-screen">
      <div className="screen-header">
        <h2>📅 Meus Agendamentos</h2>
        <p>{appointments.length} agendamento(s) encontrado(s)</p>
      </div>
      
      {appointments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📭</div>
          <h3>Nenhum agendamento</h3>
          <p>Você ainda não fez nenhum agendamento.</p>
          <button onClick={goBack} className="btn-primary">
            Fazer primeiro agendamento
          </button>
        </div>
      ) : (
        <>
          <div className="filter-buttons">
            <button 
              className={`filter-btn ${filter === 'todos' ? 'active' : ''}`}
              onClick={() => setFilter('todos')}
            >
              Todos ({appointments.length})
            </button>
            <button 
              className={`filter-btn ${filter === 'confirmado' ? 'active' : ''}`}
              onClick={() => setFilter('confirmado')}
            >
              Confirmados ({appointments.filter(a => a.status === 'confirmado').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'aguardando_pagamento' ? 'active' : ''}`}
              onClick={() => setFilter('aguardando_pagamento')}
            >
              Aguardando ({appointments.filter(a => a.status === 'aguardando_pagamento').length})
            </button>
            <button 
              className={`filter-btn ${filter === 'cancelado' ? 'active' : ''}`}
              onClick={() => setFilter('cancelado')}
            >
              Cancelados ({appointments.filter(a => a.status === 'cancelado').length})
            </button>
          </div>
          
          <div className="appointments-list">
            {filteredAppointments.map((app) => (
              <div 
                key={app.id} 
                className="appointment-card"
                style={{ borderLeftColor: getStatusColor(app.status) }}
              >
                <div className="appointment-header">
                  <div className="appointment-status">
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(app.status) }}
                    >
                      {getStatusText(app.status)}
                    </span>
                    <span className="appointment-date">
                      {formatDate(app.data, app.hora)}
                    </span>
                  </div>
                  <div className="appointment-price">
                    R$ {app.preco?.toFixed(2) || '0.00'}
                  </div>
                </div>
                
                <div className="appointment-body">
                  <h3>{app.servico}</h3>
                  <div className="appointment-details">
                    <p><strong>Barbeiro:</strong> {app.barbeiro || 'Não especificado'}</p>
                    <p><strong>Pagamento:</strong> {app.pagamento === 'pix' ? 'PIX' : 'Na hora'}</p>
                    {app.txid && <p><strong>TXID PIX:</strong> {app.txid}</p>}
                  </div>
                </div>
                
                <div className="appointment-actions">
                  {app.status === 'confirmado' && (
                    <button 
                      className="btn-cancel"
                      onClick={() => onCancel(app.id)}
                    >
                      Cancelar
                    </button>
                  )}
                  
                  {app.status === 'aguardando_pagamento' && (
                    <button 
                      className="btn-primary"
                      onClick={() => alert('Redirecionando para pagamento PIX...')}
                    >
                      Pagar Agora
                    </button>
                  )}
                  
                  <button className="btn-secondary">
                    Detalhes
                  </button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="stats-card">
            <h3>📊 Estatísticas</h3>
            <div className="stats-grid">
              <div className="stat">
                <span className="stat-label">Total</span>
                <span className="stat-value">{appointments.length}</span>
              </div>
              <div className="stat">
                <span className="stat-label">Valor Total</span>
                <span className="stat-value">
                  R$ {appointments
                    .filter(a => a.status !== 'cancelado')
                    .reduce((sum, app) => sum + (app.preco || 0), 0)
                    .toFixed(2)}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Confirmados</span>
                <span className="stat-value">
                  {appointments.filter(a => a.status === 'confirmado').length}
                </span>
              </div>
              <div className="stat">
                <span className="stat-label">Cancelados</span>
                <span className="stat-value">
                  {appointments.filter(a => a.status === 'cancelado').length}
                </span>
              </div>
            </div>
          </div>
        </>
      )}
      
      <div className="screen-actions">
        <button onClick={goBack} className="btn-primary">
          Voltar para Home
        </button>
      </div>
    </div>
  );
}

// Tela de Serviços
function ServicosScreen({ goBack, services }) {
  return (
    <div className="servicos-screen">
      <div className="screen-header">
        <h2>✂️ Nossos Serviços</h2>
        <p>Conheça todos nossos serviços e preços</p>
      </div>
      
      <div className="servicos-grid">
        {services.map(servico => (
          <div key={servico.id} className="servico-card">
            <div className="servico-header">
              <h3>{servico.nome}</h3>
              <span className="servico-price">R$ {servico.preco}</span>
            </div>
            <p className="servico-desc">{servico.descricao}</p>
            <div className="servico-footer">
              <span className="servico-duration">⏱️ {servico.duracao} min</span>
              {servico.ativo ? (
                <span className="servico-status active">Disponível</span>
              ) : (
                <span className="servico-status inactive">Indisponível</span>
              )}
            </div>
          </div>
        ))}
      </div>
      
      <div className="promo-banner">
        <h3>🎁 Promoção PIX!</h3>
        <p>Ganhe 5% de desconto pagando com PIX online!</p>
      </div>
      
      <div className="screen-actions">
        <button onClick={goBack} className="btn-primary">
          Voltar
        </button>
      </div>
    </div>
  );
}

// Tela de Pagamento
function PaymentScreen({ step, appointmentData, onPixSuccess, onBack, onComplete }) {
  if (step === 'pix' && appointmentData) {
    return (
      <div className="payment-screen">
        <PixPayment
          value={appointmentData.preco * 0.95} // 5% de desconto
          appointmentId={appointmentData.id}
          userName={appointmentData.nome}
          onSuccess={onPixSuccess}
          onCancel={onBack}
        />
      </div>
    );
  }
  
  return (
    <div className="payment-screen">
      <div className="payment-success">
        <div className="success-icon">✅</div>
        <h3>Agendamento Confirmado!</h3>
        <p>Seu agendamento foi salvo com sucesso.</p>
        <button onClick={onComplete} className="btn-primary">
          Voltar para Home
        </button>
      </div>
    </div>
  );
}

// Dashboard do Barbeiro
function BarbeiroDashboard({ goBack, appointments, onCancel }) {
  const hoje = new Date().toISOString().split('T')[0];
  const agendamentosHoje = appointments.filter(app => app.data === hoje);
  const totalReceita = appointments
    .filter(app => app.status !== 'cancelado')
    .reduce((sum, app) => sum + (app.preco || 0), 0);

  return (
    <div className="barbeiro-dashboard">
      <div className="screen-header">
        <h2>👨‍✂️ Painel do Barbeiro</h2>
        <p>Gerencie todos os agendamentos</p>
      </div>
      
      <div className="dashboard-stats">
        <div className="stat-card">
          <h3>Total Agendamentos</h3>
          <p className="stat-number">{appointments.length}</p>
        </div>
        <div className="stat-card">
          <h3>Hoje</h3>
          <p className="stat-number">{agendamentosHoje.length}</p>
        </div>
        <div className="stat-card">
          <h3>Receita Total</h3>
          <p className="stat-number">R$ {totalReceita.toFixed(2)}</p>
        </div>
        <div className="stat-card">
          <h3>Taxa de Cancelamento</h3>
          <p className="stat-number">
            {appointments.length > 0 
              ? ((appointments.filter(a => a.status === 'cancelado').length / appointments.length) * 100).toFixed(1)
              : 0}%
          </p>
        </div>
      </div>
      
      <div className="appointments-table">
        <h3>📋 Todos os Agendamentos</h3>
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Data</th>
                <th>Cliente</th>
                <th>Serviço</th>
                <th>Valor</th>
                <th>Status</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {appointments.map(app => (
                <tr key={app.id}>
                  <td>{new Date(app.data).toLocaleDateString('pt-BR')} {app.hora}</td>
                  <td>{app.nome}</td>
                  <td>{app.servico}</td>
                  <td>R$ {app.preco?.toFixed(2)}</td>
                  <td>
                    <span className={`status-badge ${app.status}`}>
                      {app.status}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn-small"
                      onClick={() => alert(`Detalhes: ${JSON.stringify(app, null, 2)}`)}
                    >
                      Ver
                    </button>
                    {app.status === 'confirmado' && (
                      <button 
                        className="btn-small btn-danger"
                        onClick={() => onCancel(app.id)}
                      >
                        Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      
      <div className="screen-actions">
        <button onClick={goBack} className="btn-primary">
          Voltar
        </button>
      </div>
    </div>
  );
}

export default App;